import React from 'react';
import { useSelector, useDispatch } from 'react-redux';
import { useDrag, useDrop } from 'react-dnd';
import { RootState } from '../../store';
import { movePlayerToSubstitutes, removeSubstitute } from '../../store/teamSlice';
import { Player, Position } from '../../types';
import './SubstituteList.css';

// Define item types for dragging
const ItemTypes = {
  PLAYER: 'player',
  SUBSTITUTE: 'substitute'
};

// Maximum number of substitutes allowed
const MAX_SUBSTITUTES = 7;

// Helper function to format player name (for display purposes) - same as in SoccerField
const formatPlayerName = (name: string | undefined): string => {
  // Handle undefined or empty name
  if (!name) return 'Player';
  
  // If name is already short, return as is
  if (name.length <= 15) return name;
  
  // For longer names, show first name and last initial
  const nameParts = name.split(' ');
  if (nameParts.length === 1) return name; // Single name
  
  const firstName = nameParts[0];
  const lastName = nameParts[nameParts.length - 1];
  
  return `${firstName} ${lastName.charAt(0)}.`;
};

// Define a component for a draggable player - similar to the one in SoccerField
const DraggablePlayer: React.FC<{
  player: Player;
  position?: Position;
  onRemove: (player: Player, e: React.MouseEvent) => void;
  onPlayerClick: (player: Player) => void;
}> = ({ player, position, onRemove, onPlayerClick }) => {
  const [{isDragging}, drag] = useDrag(() => ({
    type: ItemTypes.SUBSTITUTE,
    item: () => ({
      substitute: {
        ...player,
        id: player.id,
        position: position || null,
        isSubstitute: true
      }
    }),
    collect: (monitor) => ({
      isDragging: monitor.isDragging()
    }),
    end: (item, monitor) => {
      if (!monitor.didDrop()) {
        // Substitute was not dropped on a valid target
      }
    }
  }), [player, position]);

  const handlePlayerClick = (e: React.MouseEvent) => {
    // Don't trigger if clicking the remove button
    if ((e.target as HTMLElement).closest('.remove-player-btn')) {
      return;
    }
    e.stopPropagation();
    onPlayerClick(player);
  };

  return (
    <div 
      // @ts-ignore - React DnD typing issue
      ref={drag}
      className="player-info"
      style={{ opacity: isDragging ? 0.5 : 1 }}
      onClick={handlePlayerClick}
    >
      <img src={player.imageUrl || 'https://via.placeholder.com/150/4CAF50/FFFFFF?text=?'} alt={player.name || 'Player'} className="player-image" />
      <div className="player-name" title={player.name || 'Player'}>{formatPlayerName(player.name)}</div>
      <button 
        className="remove-player-btn"
        onClick={(e) => onRemove(player, e)}
        title="Remove player"
      >
        ×
      </button>
    </div>
  );
};

interface SubstituteListProps {
  onPlayerClick?: (player: Player, position: Position) => void;
}

const SubstituteList: React.FC<SubstituteListProps> = ({ onPlayerClick }) => {
  const dispatch = useDispatch();
  const { substitutes } = useSelector((state: RootState) => state.team.team);
  
  // Check if substitutes limit is reached
  const isSubLimitReached = substitutes.length >= MAX_SUBSTITUTES;
  
  // Setup drop handling for the substitutes container
  const [{ isOver, canDrop }, drop] = useDrop(() => ({
    accept: ItemTypes.PLAYER,
    canDrop: (item: any) => {
      // Only allow dropping if we're under the substitute limit
      return !!item.player && !isSubLimitReached;
    },
    drop: (item: any) => {
      if (item.player) {
        // It's a player from the field - move to substitutes
        dispatch(movePlayerToSubstitutes(item.player.position));
      }
    },
    collect: (monitor) => ({
      isOver: !!monitor.isOver(),
      canDrop: !!monitor.canDrop()
    })
  }), [isSubLimitReached]);

  // Function to remove substitute
  const handleRemoveSubstitute = (player: Player, e: React.MouseEvent) => {
    e.stopPropagation(); // Prevent triggering other events
    dispatch(removeSubstitute(player.id));
  };

  // Handle player click to show info card
  const handlePlayerClick = (player: Player) => {
    if (onPlayerClick) {
      onPlayerClick(player, player.position);
    }
  };
  
  return (
    <div className="substitute-list">
      <h3 className={isSubLimitReached ? 'subs-limit-reached' : ''}>
        Substitutes ({substitutes.length}/{MAX_SUBSTITUTES})
        {isSubLimitReached && (
          <span className="subs-limit-indicator"> - Maximum reached</span>
        )}
      </h3>
      
      <div className="substitutes-drag-hint">
        <i className="drag-icon">↕</i> Drag players to and from the field
      </div>
      
      {/* Use the drop ref for the substitutes container to accept players from the field */}
      <div 
        // @ts-ignore - Using ref from React DnD
        ref={drop} 
        className={`substitutes-container ${isOver && canDrop ? 'field-drop-target' : ''} ${isSubLimitReached ? 'subs-limit-reached' : ''}`}
      >
        {substitutes.length === 0 ? (
          <div className="no-substitutes">
            Drag players here from the field or add new substitutes
          </div>
        ) : (
          substitutes.map((substitute) => (
            <div key={substitute.id} className="substitute-card">
              <DraggablePlayer 
                player={substitute}
                onRemove={handleRemoveSubstitute}
                onPlayerClick={handlePlayerClick}
              />
            </div>
          ))
        )}
        
        {isSubLimitReached && (
          <div className="substitutes-limit-message">
            Maximum of {MAX_SUBSTITUTES} substitutes reached
          </div>
        )}
      </div>

    </div>
  );
};

export default SubstituteList; 